"use client"

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  type ReactNode,
} from "react"
import dynamic from "next/dynamic"

import { json, jsonParseLinter } from "@codemirror/lang-json"
import { linter, lintGutter, type Diagnostic } from "@codemirror/lint"
import { EditorView } from "@codemirror/view"
import { CopyButton } from "@coval/ui"
import { useTheme } from "next-themes"

import { editorHeightForRows, editorTypographyTheme } from "./codeMirrorTheme"
import { ReadOnlyCopyButton } from "./version-history/ReadOnlyCopyButton"
import { useIsInReadOnlyRegion } from "./version-history/ReadOnlyRegion"

// The editor view needs the DOM, so load it client-side only (mirrors how
// SqlEditor lazy-loads CodeMirror).
const COPY_BUTTON_CLASS_NAME =
  "pointer-events-none absolute right-1 top-1 z-10 opacity-0 transition-opacity group-hover/json-editor:pointer-events-auto group-hover/json-editor:opacity-100 group-focus-within/json-editor:pointer-events-auto group-focus-within/json-editor:opacity-100"

const CodeMirror = dynamic(() => import("@uiw/react-codemirror"), {
  ssr: false,
})

const parseJsonDocument = jsonParseLinter()

/**
 * Reports JSON parse errors inline. A blank document reports nothing, because
 * an empty optional field is not a syntax error the user needs to see.
 */
export function jsonDiagnostics(view: EditorView): Diagnostic[] {
  return view.state.doc.toString().trim() === "" ? [] : parseJsonDocument(view)
}

const jsonSyntaxLinter = linter(jsonDiagnostics)

// CodeMirror's bundled themes supply blue-gray editor chrome. Map its interactive
// states to the same semantic colors as the surrounding Coval UI.
const semanticSurfaceTheme = EditorView.theme({
  "&": {
    backgroundColor: "var(--surface-default) !important",
    color: "var(--coval-text) !important",
  },
  ".cm-content": {
    caretColor: "var(--coval-text) !important",
  },
  ".cm-cursor, .cm-dropCursor": {
    borderLeftColor: "var(--coval-text) !important",
  },
  "&.cm-focused > .cm-scroller > .cm-selectionLayer .cm-selectionBackground, .cm-selectionBackground, .cm-content ::selection":
    {
      backgroundColor: "var(--surface-selected) !important",
    },
  "&.cm-focused .cm-matchingBracket": {
    backgroundColor: "var(--surface-selected) !important",
  },
  "&.cm-focused .cm-nonmatchingBracket": {
    backgroundColor: "var(--coval-danger-surface) !important",
  },
  ".cm-gutters": {
    backgroundColor: "var(--surface-default) !important",
  },
  ".cm-activeLine, .cm-activeLineGutter": {
    backgroundColor: "var(--surface-1) !important",
  },
  ".cm-tooltip": {
    backgroundColor: "var(--surface-0) !important",
    color: "var(--coval-text) !important",
    border: "1px solid var(--coval-border) !important",
  },
  ".cm-tooltip .cm-tooltip-arrow:after": {
    borderTopColor: "var(--surface-0) !important",
    borderBottomColor: "var(--surface-0) !important",
  },
  ".cm-lintRange-active": {
    backgroundColor: "var(--surface-selected) !important",
  },
  ".cm-lintRange-error": {
    backgroundImage: "none !important",
    textDecoration: "underline wavy var(--coval-danger-border)",
  },
  ".cm-lintPoint-error:after": {
    borderBottomColor: "var(--coval-danger-border) !important",
  },
})

const copyButtonSpaceTheme = EditorView.theme({
  ".cm-content": { paddingRight: "40px" },
})

const AUTO_SIZE_MIN_LINES = 3
const DEFAULT_ROWS = 4
const DEFAULT_MAX_HEIGHT = "320px"

const JsonEditorAutoSizeContext = createContext<number | undefined>(undefined)

export function useJsonEditorAutoSizeMaxLines() {
  return useContext(JsonEditorAutoSizeContext)
}

/** Apply the same content-based sizing to JSON fields within a form. */
export function JsonEditorAutoSizeProvider({
  maxLines,
  children,
}: {
  maxLines: number
  children: ReactNode
}) {
  return (
    <JsonEditorAutoSizeContext.Provider value={maxLines}>
      {children}
    </JsonEditorAutoSizeContext.Provider>
  )
}

/**
 * One of the two naming props is mandatory, mirroring `@coval/ui`'s `Textarea`,
 * so an unnamed editor fails typecheck rather than shipping an unlabeled
 * textbox to a screen reader.
 */
type AccessibleName =
  | { ariaLabel: string; ariaLabelledBy?: string }
  | { ariaLabel?: string; ariaLabelledBy: string }

interface JsonCodeEditorOwnProps {
  /** The JSON text. Raw text rather than a parsed object, so partially typed and templated values survive. */
  value: string
  /** Called with the new text on every edit. */
  onChange: (value: string) => void
  /** Called when the editor gains focus. */
  onFocus?: () => void
  /** Called when the editor loses focus, for consumers that reformat or commit on blur. */
  onBlur?: () => void
  /** Placeholder shown when the editor is empty. */
  placeholder?: string
  /** Fixed editor height. Omit to let the editor grow from `rows` up to `maxHeight`. */
  height?: string
  /** Lines shown before the editor starts growing (default: 4). Ignored when `height` is set. */
  rows?: number
  /** Height ceiling when `height` is omitted, past which the editor scrolls (default: "320px"). */
  maxHeight?: string
  /** Grow from three lines through this many lines, then scroll. Overrides fixed `height` and `rows`; 0 opts out of a surrounding provider. */
  autoSizeMaxLines?: number
  /** Optional className for the container. */
  className?: string
  /** Id of an element describing the editor, typically the help text below it. */
  ariaDescribedBy?: string
  /** Whether to announce the field as required. */
  ariaRequired?: boolean
  /** Whether the editor is non-editable and dimmed. */
  disabled?: boolean
  /** Whether the editor is non-editable but still focusable and undimmed. */
  readOnly?: boolean
  /**
   * Strips the border, line numbers, and gutters, for editors that sit inline
   * in a dense surface such as a table cell alongside plain inputs.
   */
  chromeless?: boolean
  /** Hides line numbers and lint gutters while keeping inline JSON diagnostics. */
  hideGutters?: boolean
  /** Shows a control that copies the editor's current text. */
  copyable?: boolean
  /**
   * Whether to underline JSON parse errors. Turn this off for fields that hold
   * template variables such as `{{agent.api_key}}`, which are not valid JSON on
   * their own; those fields validate through `error` instead.
   */
  lint?: boolean
  /** Validation message owned by the consumer. Renders below the editor and reddens its border. */
  error?: string | null
}

type JsonCodeEditorProps = JsonCodeEditorOwnProps & AccessibleName

/**
 * A JSON editor with syntax highlighting, built on CodeMirror 6 via
 * `@uiw/react-codemirror` and the official `@codemirror/lang-json` language
 * package. Lazy-loaded client-side and themed from next-themes, mirroring
 * {@link SqlEditor}.
 *
 * This is the text-level counterpart to `JsonEditor`, which takes and returns a
 * parsed object. Prefer this one for form fields, where the value is a string
 * the user is midway through typing and may contain template variables.
 */
export function JsonCodeEditor({
  value,
  onChange,
  onFocus,
  onBlur,
  placeholder,
  height,
  rows = DEFAULT_ROWS,
  maxHeight = DEFAULT_MAX_HEIGHT,
  autoSizeMaxLines,
  className = "",
  ariaLabel,
  ariaLabelledBy,
  ariaDescribedBy,
  ariaRequired,
  disabled = false,
  readOnly = false,
  chromeless = false,
  hideGutters = false,
  copyable = false,
  lint = true,
  error,
}: JsonCodeEditorProps) {
  const { resolvedTheme } = useTheme()
  const isInReadOnlyRegion = useIsInReadOnlyRegion()
  const inheritedMaxLines = useJsonEditorAutoSizeMaxLines()
  const effectiveMaxLines = autoSizeMaxLines ?? inheritedMaxLines

  // Held in refs so inline handlers at the call sites do not change identity
  // every render, which would make @uiw reconfigure the whole extension set on
  // each keystroke.
  const onChangeRef = useRef(onChange)
  const onFocusRef = useRef(onFocus)
  const onBlurRef = useRef(onBlur)
  useEffect(() => {
    onChangeRef.current = onChange
    onFocusRef.current = onFocus
    onBlurRef.current = onBlur
  }, [onChange, onFocus, onBlur])

  const handleChange = useCallback((next: string) => {
    onChangeRef.current(next)
  }, [])

  const extensions = useMemo(() => {
    // @uiw spreads unrecognized props onto its wrapper div, which carries no
    // role, so naming attributes have to be put on the content element itself.
    const contentAttributes: Record<string, string> = {}
    if (ariaLabel) {
      contentAttributes["aria-label"] = ariaLabel
    }
    if (ariaLabelledBy) {
      contentAttributes["aria-labelledby"] = ariaLabelledBy
    }
    if (ariaDescribedBy) {
      contentAttributes["aria-describedby"] = ariaDescribedBy
    }
    if (ariaRequired) {
      contentAttributes["aria-required"] = "true"
    }

    const active = [
      json(),
      EditorView.lineWrapping,
      semanticSurfaceTheme,
      editorTypographyTheme,
      EditorView.contentAttributes.of(contentAttributes),
      EditorView.domEventHandlers({
        focus: () => {
          onFocusRef.current?.()
        },
        blur: () => {
          onBlurRef.current?.()
        },
      }),
    ]
    if (copyable) {
      active.push(copyButtonSpaceTheme)
    }
    if (lint) {
      active.push(jsonSyntaxLinter)
      if (!chromeless && !hideGutters) {
        active.push(lintGutter())
      }
    }
    return active
  }, [
    lint,
    chromeless,
    hideGutters,
    copyable,
    ariaLabel,
    ariaLabelledBy,
    ariaDescribedBy,
    ariaRequired,
  ])

  // A caller-set `height` is usually "100%", which only resolves if the
  // wrapper between it and the caller's sized container has a height too.
  const editorHeight = effectiveMaxLines ? undefined : height
  const fillsContainer = editorHeight ? "h-full" : ""

  const setup = useMemo(
    () => ({
      foldGutter: false,
      highlightActiveLine: false,
      lineNumbers: !chromeless && !hideGutters,
      highlightActiveLineGutter: !chromeless && !hideGutters,
    }),
    [chromeless, hideGutters],
  )

  return (
    <div className={`${fillsContainer} ${className}`}>
      <div
        className={`group/json-editor relative overflow-hidden ${fillsContainer} ${
          chromeless
            ? ""
            : `rounded border ${error ? "border-coval-danger-border" : "border-coval-border"}`
        } ${disabled ? "opacity-60" : ""}`}
      >
        <CodeMirror
          value={value}
          onChange={handleChange}
          height={editorHeight}
          minHeight={
            editorHeight
              ? undefined
              : `${editorHeightForRows(effectiveMaxLines ? AUTO_SIZE_MIN_LINES : rows)}px`
          }
          maxHeight={
            editorHeight
              ? undefined
              : effectiveMaxLines
                ? `${editorHeightForRows(effectiveMaxLines)}px`
                : maxHeight
          }
          theme={resolvedTheme === "dark" ? "dark" : "light"}
          extensions={extensions}
          placeholder={placeholder}
          editable={!disabled}
          readOnly={readOnly || isInReadOnlyRegion}
          // Tab moves focus out of the editor like any other form field, so
          // keyboard users are never trapped inside it.
          indentWithTab={false}
          basicSetup={setup}
        />
        {copyable &&
          (isInReadOnlyRegion ? (
            <ReadOnlyCopyButton
              value={value}
              label="Copy JSON"
              className={COPY_BUTTON_CLASS_NAME}
            />
          ) : (
            <CopyButton
              value={value}
              label="Copy JSON"
              className={COPY_BUTTON_CLASS_NAME}
            />
          ))}
      </div>
      {error && <p className="mt-1 text-xs text-coval-danger-text">{error}</p>}
    </div>
  )
}
